import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { FaWhatsapp } from 'react-icons/fa';
import {
  FiAlertCircle,
  FiAward,
  FiCalendar,
  FiCamera,
  FiCheckCircle,
  FiChevronRight,
  FiClock,
  FiCopy,
  FiDollarSign,
  FiEdit3,
  FiExternalLink,
  FiEye,
  FiHeart,
  FiMapPin,
  FiMenu,
  FiMessageCircle,
  FiShare2,
  FiShield,
  FiStar,
  FiTarget,
  FiUsers,
  FiX,
} from 'react-icons/fi';
import * as S from './styles';
import { theme, VerificationStatus } from './styles';
import { Donut, DonutSlice, ScoreRing } from './charts';
import api from '../../../services/api';
import { useAuth } from '../../../hooks/AuthContext';
import { useSocialShare } from '../../../hooks/SocialShareContext';
import { useToast } from '../../../hooks/toast';
import { capitalizeUserName } from '../../../utils/formatString';
import Sidebar from '../../../components/Sidebar';
import {
  CaregiverDocument,
  DocumentType,
  listDocuments,
} from '../../../services/documents';
import {
  CaregiverMetrics,
  METRICS_PERIODS,
  MetricsPeriod,
  RegionStats,
  getMetrics,
  getRegionStats,
} from '../../../services/metrics';

const imgNoProfile = 'https://app-cuid.s3.amazonaws.com/no-avatar.png';

// A bio this long already tells families about experience and specialties
const GOOD_BIO_LENGTH = 200;

const DOCUMENT_LABEL: Partial<Record<DocumentType, string>> = {
  document_front: 'documento com foto (frente)',
  document_back: 'documento com foto (verso)',
  resume: 'currículo',
  certificate: 'certificado',
};

const STATUS_COPY: Record<VerificationStatus, string> = {
  verified: 'Verificado',
  in_review: 'Documentos em análise',
  unverified: 'Não verificado',
};

const STATUS_ICON: Record<VerificationStatus, JSX.Element> = {
  verified: <FiCheckCircle />,
  in_review: <FiClock />,
  unverified: <FiAlertCircle />,
};

const DONUT_COLORS = [theme.green, theme.blue, theme.purple, theme.muted];

interface Opportunity {
  key: string;
  tone: 'danger' | 'warning' | 'info';
  icon: JSX.Element;
  title: string;
  description: string;
  onClick(): void;
}

// Shift costs are stored in cents
function formatMoney(value: number | null): string {
  if (value === null) return '—';

  return (value / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
}

// undefined while loading, null when the request failed
function placeholder(value: unknown): string {
  return value === undefined ? '…' : '—';
}

function formatPercent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

const Dashboard: React.FC = () => {
  const { user, updateCaregiver } = useAuth();
  const { toggleSocialShare } = useSocialShare();
  const { addToast } = useToast();
  const history = useHistory();

  const [isMenuOpen, setMenuOpen] = useState(false);
  const [documents, setDocuments] = useState<CaregiverDocument[] | null>(null);
  const [region, setRegion] = useState<RegionStats | null>();
  const [period, setPeriod] = useState<MetricsPeriod>(30);
  const [metrics, setMetrics] = useState<CaregiverMetrics | null>();

  const firstName = capitalizeUserName(user.name).split(' ')[0];
  const profileUrl = `https://app.cuid.com.br/me/${user.slug}`;
  const hasFormation = String(user.formation) === 'true';
  const bioLength = (user.bio || '').trim().length;
  const yourCost = Number(user.cost) > 0 ? Number(user.cost) : null;

  useEffect(() => {
    listDocuments()
      .then(response => {
        setDocuments(response.documents);

        // The stored user is stale after an admin approves the documents
        if (response.verified !== user.verified) {
          updateCaregiver({ ...user, verified: response.verified });
        }
      })
      .catch(() => setDocuments([]));

    getRegionStats()
      .then(setRegion)
      .catch(() => setRegion(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setMetrics(undefined);

    getMetrics(period)
      .then(setMetrics)
      .catch(() => setMetrics(null));
  }, [period]);

  const makePayment = async () => {
    const stripe = await loadStripe(
      'pk_live_51IcJg5FxYI3sgKUsiC2NoBcCJoeWz2bep1hQZ4ZGj0onr7F8gH8koJoBijSzWIry3k7VLiOByKnn7v426j47zqRT00qxUoWYF6',
    );

    const response = await api.post('payments/create-checkout-session');

    const session = await response.data;
    const result = stripe?.redirectToCheckout({
      sessionId: session.id,
    });

    if ((await result)?.error) {
      console.log((await result)?.error);
    }
  };

  const copyProfileLink = async () => {
    try {
      await navigator.clipboard.writeText(profileUrl);
      addToast({ type: 'success', title: 'Link do perfil copiado!' });
    } catch {
      addToast({
        type: 'error',
        title: 'Não foi possível copiar',
        description: profileUrl,
      });
    }
  };

  const whatsappShareUrl = `https://wa.me/?text=${encodeURIComponent(
    `Olá! Veja meu perfil profissional na Cuid: ${profileUrl}`,
  )}`;

  // Documents
  const requiredDocuments: DocumentType[] = [
    'document_front',
    'document_back',
    'resume',
    ...(hasFormation ? (['certificate'] as DocumentType[]) : []),
  ];

  const sentDocuments = documents || [];
  const rejected = sentDocuments.find(
    document => document.status === 'rejected',
  );
  const validDocuments = requiredDocuments.filter(type =>
    sentDocuments.some(
      document => document.type === type && document.status !== 'rejected',
    ),
  );
  const missingDocuments = requiredDocuments.filter(
    type => !sentDocuments.some(document => document.type === type),
  );

  let verification: VerificationStatus = 'unverified';
  if (user.verified) {
    verification = 'verified';
  } else if (documents && missingDocuments.length === 0 && !rejected) {
    verification = 'in_review';
  }

  // Profile score: only what the caregiver controls and we can measure
  const scoreItems = [
    { label: 'Foto de perfil', value: user.avatar_url ? 100 : 0 },
    {
      label: 'Resumo',
      value: Math.min(100, Math.round((bioLength / GOOD_BIO_LENGTH) * 100)),
    },
    {
      label: 'Documentação',
      value: user.verified
        ? 100
        : Math.round((validDocuments.length / requiredDocuments.length) * 100),
    },
    { label: 'Valor do plantão', value: yourCost ? 100 : 0 },
  ];
  const score = documents
    ? Math.round(
        scoreItems.reduce((sum, item) => sum + item.value, 0) /
          scoreItems.length,
      )
    : null;

  let insight = 'Perfis completos passam mais confiança para as famílias.';
  if (region && region.professionals > 0) {
    if (user.verified && region.verified_rate < 0.5) {
      insight = `Seu perfil é verificado, algo que só ${formatPercent(
        region.verified_rate,
      )} dos profissionais da sua região têm.`;
    } else if (user.avatar_url && region.photo_rate < 1) {
      insight = `Você tem foto de perfil e ${formatPercent(
        1 - region.photo_rate,
      )} dos profissionais da sua região não têm.`;
    }
  }

  // Your cost position inside the regional range
  const costRange =
    region && region.cost.min !== null && region.cost.max !== null
      ? { min: region.cost.min, max: region.cost.max }
      : null;
  const costPosition =
    costRange && yourCost && costRange.max > costRange.min
      ? Math.max(
          0,
          Math.min(
            100,
            ((yourCost - costRange.min) / (costRange.max - costRange.min)) *
              100,
          ),
        )
      : 50;

  // Occupations donut: top 3 plus "Outros"
  const occupations = region ? region.occupations : [];
  const occupationSlices: DonutSlice[] = occupations
    .slice(0, 3)
    .map((item, index) => ({
      label: item.occupation,
      value: item.count,
      color: DONUT_COLORS[index],
    }));
  const otherOccupations = occupations
    .slice(3)
    .reduce((sum, item) => sum + item.count, 0);
  if (otherOccupations > 0) {
    occupationSlices.push({
      label: 'Outros',
      value: otherOccupations,
      color: DONUT_COLORS[3],
    });
  }

  // Opportunities, most blocking first
  const opportunities: Opportunity[] = [];
  if (rejected) {
    opportunities.push({
      key: 'rejected',
      tone: 'danger',
      icon: <FiAlertCircle />,
      title: `Reenviar ${DOCUMENT_LABEL[rejected.type] || 'documento'}`,
      description: rejected.rejection_reason || 'O documento foi recusado.',
      onClick: () => history.push('/documentos'),
    });
  }
  if (documents && missingDocuments.length > 0 && !user.verified) {
    opportunities.push({
      key: 'documents',
      tone: 'warning',
      icon: <FiShield />,
      title: 'Enviar seus documentos',
      description:
        missingDocuments.length === 1
          ? 'Falta 1 documento para o selo de verificado.'
          : `Faltam ${missingDocuments.length} documentos para o selo de verificado.`,
      onClick: () => history.push('/documentos'),
    });
  }
  if (!user.avatar_url) {
    opportunities.push({
      key: 'photo',
      tone: 'warning',
      icon: <FiCamera />,
      title: 'Adicionar foto de perfil',
      description: 'Perfis com foto aparecem antes nas buscas das famílias.',
      onClick: () => history.push('/perfil/editar'),
    });
  }
  if (bioLength < GOOD_BIO_LENGTH) {
    opportunities.push({
      key: 'bio',
      tone: 'info',
      icon: <FiEdit3 />,
      title: 'Completar seu resumo',
      description: 'Conte sua experiência e as situações que você já atendeu.',
      onClick: () => history.push('/perfil/editar'),
    });
  }
  if (yourCost && region && region.cost.max && yourCost > region.cost.max) {
    opportunities.push({
      key: 'cost',
      tone: 'info',
      icon: <FiDollarSign />,
      title: 'Revisar o valor do plantão',
      description: `Seu valor está acima da faixa da região (até ${formatMoney(
        region.cost.max,
      )}).`,
      onClick: () => history.push('/perfil/editar'),
    });
  }

  return (
    <S.Layout>
      <S.SidebarSlot open={isMenuOpen}>
        <Sidebar />
      </S.SidebarSlot>

      <div>
        <S.Topbar>
          <button
            type="button"
            className="menu-toggle"
            aria-label="Abrir menu"
            onClick={() => setMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <FiX /> : <FiMenu />}
          </button>

          <Link to="/perfil/editar" className="user">
            <img src={user.avatar_url || imgNoProfile} alt={user.name} />
            <div>
              <strong>{capitalizeUserName(user.name)}</strong>
              <span>{user.occupation}</span>
            </div>
          </Link>
        </S.Topbar>

        <S.Content>
          <S.Main>
            <S.PageTitle className="order-title">
              <div>
                <h1>Olá, {firstName}! 👋</h1>
                <p>
                  Veja como está seu perfil na Cuid e como ele se compara aos
                  profissionais da sua região.
                </p>
              </div>

              <div className="filters">
                <S.Chip>
                  <FiMapPin />
                  {user.city}, {user.uf}
                </S.Chip>
                <S.Chip>
                  <FiCalendar />
                  <select
                    aria-label="Período"
                    value={period}
                    onChange={e =>
                      setPeriod(Number(e.target.value) as MetricsPeriod)
                    }
                  >
                    {METRICS_PERIODS.map(days => (
                      <option key={days} value={days}>
                        Últimos {days} dias
                      </option>
                    ))}
                  </select>
                </S.Chip>
              </div>
            </S.PageTitle>

            <S.Highlights className="order-highlights">
              <S.PremiumHighlight active={!!user.payment_status}>
                <span className="badge">
                  <FiStar />
                  Premium
                </span>

                {user.payment_status ? (
                  <>
                    <h2>Você é Premium</h2>
                    <ul>
                      <li>
                        <FiCheckCircle />
                        Prioridade nas vagas de parceiros da Cuid
                      </li>
                      <li>
                        <FiCheckCircle />
                        Perfil disponível para famílias de todo o Brasil
                      </li>
                    </ul>
                  </>
                ) : (
                  <>
                    <h2>Saia na frente nas vagas</h2>
                    <ul>
                      <li>
                        <FiCheckCircle />
                        Prioridade nas vagas de parceiros da Cuid
                      </li>
                      <li>
                        <FiCheckCircle />
                        Perfil disponível para famílias de todo o Brasil
                      </li>
                    </ul>

                    {metrics && metrics.profile_views > 0 && (
                      <p className="context">
                        Seu perfil teve {metrics.profile_views}{' '}
                        {metrics.profile_views === 1 ? 'visita' : 'visitas'} nos
                        últimos {period} dias. Aproveite esse interesse.
                      </p>
                    )}

                    <div className="cta">
                      <S.PremiumButton type="button" onClick={makePayment}>
                        Ativar Premium
                        <FiChevronRight />
                      </S.PremiumButton>
                      <span>R$ 29,90/mês</span>
                    </div>
                  </>
                )}
              </S.PremiumHighlight>

              <S.ShareHighlight>
                <div className="icon">
                  <FaWhatsapp />
                </div>
                <h2>Divulgue seu perfil</h2>
                <p>
                  Muitos plantões chegam por indicação. Envie seu perfil para
                  conhecidos e grupos.
                </p>

                <S.WhatsappButton
                  href={whatsappShareUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaWhatsapp />
                  Compartilhar no WhatsApp
                </S.WhatsappButton>

                <div className="links">
                  <S.LinkButton type="button" onClick={copyProfileLink}>
                    <FiCopy />
                    Copiar link
                  </S.LinkButton>
                  <S.LinkButton
                    type="button"
                    onClick={() =>
                      toggleSocialShare(
                        profileUrl,
                        'Veja meu perfil profissional na Cuid',
                      )
                    }
                  >
                    <FiShare2 />
                    Outras redes
                  </S.LinkButton>
                  <S.LinkButton
                    type="button"
                    onClick={() => history.push(`/perfil/${user.slug}`)}
                  >
                    <FiExternalLink />
                    Ver perfil
                  </S.LinkButton>
                </div>
              </S.ShareHighlight>
            </S.Highlights>

            <S.Grid columns={2}>
              <S.Card tinted className="order-profile">
                <S.CardHeader>
                  <h2>Seu perfil na Cuid</h2>
                  <S.StatusBadge status={verification}>
                    {STATUS_ICON[verification]}
                    {STATUS_COPY[verification]}
                  </S.StatusBadge>
                </S.CardHeader>

                <S.Score>
                  <div className="ring">
                    <ScoreRing value={score} />
                    Perfil completo
                  </div>

                  <S.Bars>
                    {scoreItems.map(item => (
                      <li key={item.label}>
                        <span>{item.label}</span>
                        <span className="value">
                          {documents ? `${item.value}%` : '…'}
                        </span>
                        <S.Bar value={documents ? item.value : 0} />
                      </li>
                    ))}
                  </S.Bars>
                </S.Score>

                <S.Insight>
                  <FiStar />
                  {insight}
                </S.Insight>
              </S.Card>

              <S.Card>
                <S.CardHeader>
                  <h2>Como seu perfil se compara</h2>
                  <span>Em relação aos profissionais da sua região</span>
                </S.CardHeader>

                <S.Compare>
                  <div className="compare-title">
                    <FiDollarSign />
                    Valor do plantão
                  </div>
                  <div className="compare-values">
                    <div>
                      <small>Seu valor</small>
                      <strong>{formatMoney(yourCost)}</strong>
                    </div>
                    <div>
                      <small>Média da região</small>
                      <span className="regional">
                        {formatMoney(region ? region.cost.average : null)}
                      </span>
                    </div>
                    {costRange && (
                      <S.Range position={costPosition}>
                        <small>Faixa praticada na região</small>
                        <div className="track">
                          {yourCost && <div className="marker" />}
                        </div>
                        <div className="limits">
                          <span>{formatMoney(costRange.min)}</span>
                          <span>{formatMoney(costRange.max)}</span>
                        </div>
                      </S.Range>
                    )}
                  </div>
                </S.Compare>

                <S.Compare>
                  <div className="compare-title">
                    <FiShield />
                    Selo de verificado
                  </div>
                  <div className="compare-values">
                    <div>
                      <small>Você</small>
                      <strong>{user.verified ? 'Sim' : 'Ainda não'}</strong>
                    </div>
                    <div>
                      <small>Na região</small>
                      <span className="regional">
                        {region ? formatPercent(region.verified_rate) : '—'}
                      </span>
                    </div>
                    <S.Bar
                      value={region ? region.verified_rate * 100 : 0}
                      color={theme.muted}
                    />
                  </div>
                </S.Compare>
              </S.Card>
            </S.Grid>

            <S.Grid columns={2}>
              <S.Card>
                <S.CardHeader>
                  <h2>Profissionais da sua região</h2>
                  <span>Distribuição por ocupação</span>
                </S.CardHeader>

                {region && region.professionals > 0 ? (
                  <S.DonutRow>
                    <Donut
                      slices={occupationSlices}
                      centerLabel="profissionais"
                    />
                    <S.Legend>
                      {occupationSlices.map(slice => (
                        <li key={slice.label}>
                          <i style={{ background: slice.color }} />
                          <span>{slice.label}</span>
                          <strong>
                            {formatPercent(slice.value / region.professionals)}
                          </strong>
                        </li>
                      ))}
                    </S.Legend>
                  </S.DonutRow>
                ) : (
                  <S.Hint>
                    {region === undefined && 'Carregando…'}
                    {region === null &&
                      'Não foi possível carregar os dados da região.'}
                    {region &&
                      'Ainda não há outros profissionais da Cuid na sua cidade.'}
                  </S.Hint>
                )}
              </S.Card>

              <S.Card>
                <S.CardHeader>
                  <h2>Seu desempenho</h2>
                  <span>Últimos {period} dias</span>
                </S.CardHeader>

                <S.Stats columns={1}>
                  <S.Stat>
                    <FiEye />
                    <div>
                      <small>Visitas ao seu perfil</small>
                      <strong>
                        {metrics ? metrics.profile_views : placeholder(metrics)}
                      </strong>
                    </div>
                  </S.Stat>
                  <S.Stat>
                    <FiMessageCircle />
                    <div>
                      <small>Famílias pediram contato</small>
                      <strong>
                        {metrics
                          ? metrics.whatsapp_clicks
                          : placeholder(metrics)}
                      </strong>
                    </div>
                  </S.Stat>
                </S.Stats>

                {metrics &&
                  metrics.profile_views === 0 &&
                  metrics.whatsapp_clicks === 0 && (
                    <S.Hint>
                      Compartilhe seu perfil para começar a receber visitas.
                    </S.Hint>
                  )}
              </S.Card>
            </S.Grid>

            <S.Card>
              <S.CardHeader>
                <h2>Seu mercado em números</h2>
                <span>
                  Profissionais da Cuid em {user.city}, {user.uf}
                </span>
              </S.CardHeader>

              <S.Stats columns={4}>
                <S.Stat>
                  <FiUsers />
                  <div>
                    <small>Outros profissionais</small>
                    <strong>
                      {region ? region.professionals : placeholder(region)}
                    </strong>
                  </div>
                </S.Stat>
                <S.Stat>
                  <FiDollarSign />
                  <div>
                    <small>Valor médio do plantão</small>
                    <strong>
                      {region
                        ? formatMoney(region.cost.average)
                        : placeholder(region)}
                    </strong>
                  </div>
                </S.Stat>
                <S.Stat>
                  <FiAward />
                  <div>
                    <small>Com formação</small>
                    <strong>
                      {region
                        ? formatPercent(region.formation_rate)
                        : placeholder(region)}
                    </strong>
                  </div>
                </S.Stat>
                <S.Stat>
                  <FiShield />
                  <div>
                    <small>Verificados</small>
                    <strong>
                      {region
                        ? formatPercent(region.verified_rate)
                        : placeholder(region)}
                    </strong>
                  </div>
                </S.Stat>
              </S.Stats>
            </S.Card>
          </S.Main>

          <S.Aside>
            <S.Opportunities>
              <h2>
                <FiTarget />
                Oportunidades para você
              </h2>
              <p>
                O que mais aumenta suas chances de conseguir plantões agora.
              </p>

              {opportunities.length === 0 && (
                <S.Hint>Seu perfil está em dia. Continue divulgando!</S.Hint>
              )}

              <ul>
                {opportunities.slice(0, 4).map(item => (
                  <li key={item.key}>
                    <S.Opportunity
                      type="button"
                      tone={item.tone}
                      onClick={item.onClick}
                    >
                      <div className="icon">{item.icon}</div>
                      <div>
                        <strong>{item.title}</strong>
                        <span>{item.description}</span>
                      </div>
                      <FiChevronRight />
                    </S.Opportunity>
                  </li>
                ))}
              </ul>

              <S.OutlineButton
                type="button"
                onClick={() => history.push(`/perfil/${user.slug}`)}
              >
                Acessar meu perfil
                <FiChevronRight />
              </S.OutlineButton>
            </S.Opportunities>

            <S.Motto>
              <FiHeart />
              Seu cuidado faz toda a diferença.
            </S.Motto>
          </S.Aside>
        </S.Content>
      </div>
    </S.Layout>
  );
};

export default Dashboard;
